import {
  register,
  clearTokens,
  tokenAuthorize,
  refreshAccessToken as refreshOAuth2AccessToken,
} from "../api/oAuth2";
import axios from "axios";
import { useUserStore } from "../store/v2/user";
import { useClientStore } from "../store/v2/client";
import { useNotificationStore } from "../store/v2/notification";

export default {
  install: (app, options) => {
    const registerUser = async (email, password) => {
      let successful = true;
      const res = await register(email, password).catch((err) => {
        successful = false;
        return err;
      });

      if (successful) {
        const userStore = useUserStore();
        userStore.setEmail(email);
      }

      return successful;
    };

    const authorize = async (
      code,
      codeVerifier,
      redirectUri,
      clientId,
      returnResponse = false
    ) => {
      const tokenResponse = await tokenAuthorize(
        code,
        codeVerifier,
        redirectUri,
        clientId
      );

      if (tokenResponse.status === 200) {
        const userStore = useUserStore();
        await setAccessTokenValues(userStore, tokenResponse);
      }

      if (returnResponse) {
        return tokenResponse;
      }

      return false;
    };

    const isAuthorized = () => {
      return isAccessTokenValid();
    };

    const logout = (clearServerTokens = true) => {
      // Reset all stores on logout to prevent users after another authentication from seeing data from previous user
      resetStores();

      // redirect to server logout
      if (clearServerTokens) {
        clearTokens();
      }
    };

    const resetStores = () => {
      const userStore = useUserStore();
      const clientStore = useClientStore();
      const notificationStore = useNotificationStore();

      userStore.$reset();
      clientStore.$reset();
      notificationStore.$reset();
      userStore.$persist?.clear?.();

      // Set static token as authorization header when logged out
      axios.defaults.headers.common = {
        Authorization: `Bearer ${import.meta.env.VITE_STATIC_API_TOKEN}`,
      };
    };

    const isAccessTokenValid = () => {
      const userStore = useUserStore();
      const currentTime = new Date().getTime();
      const timeSafetyOffset = 5000; //5 second offset to prevent request overlap

      return (
        userStore.accessToken !== null &&
        currentTime + timeSafetyOffset <= userStore.accessTokenExpiresAt
      );
    };

    const refreshAccessToken = async () => {
      const userStore = useUserStore();
      const refreshToken = userStore.refreshToken;

      if (refreshToken !== null) {
        const res = await refreshOAuth2AccessToken(refreshToken);

        setAccessTokenValues(userStore, res);
      }
    };

    const getAccessToken = () => {
      const userStore = useUserStore();
      return userStore.getAccessToken;
    };

    const setAccessTokenValues = (userStore, res) => {
      const accessTokenExpiresAt =
        new Date().getTime() + 1000 * res.data.expires_in;
      userStore.setAccessTokenExpiresAt(accessTokenExpiresAt);
      userStore.setAccessToken(res.data.access_token);
      userStore.setRefreshToken(res.data.refresh_token);
    };

    const dec2hex = (dec) => {
      return ("0" + dec.toString(16)).slice(-2);
    };

    const sha256 = (plain) => {
      const encoder = new TextEncoder();
      const data = encoder.encode(plain);

      return window.crypto.subtle.digest("SHA-256", data);
    };

    const base64urlencode = (a) => {
      let str = "";
      const bytes = new Uint8Array(a);
      const len = bytes.byteLength;

      for (let i = 0; i < len; i++) {
        str += String.fromCharCode(bytes[i]);
      }

      return window
        .btoa(str)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
    };

    const getAuthorizationCodeVerifier = () => {
      const userStore = useUserStore();
      let authorizationCodeVerifier = userStore.getAuthorizationCodeVerifier;

      if (authorizationCodeVerifier) {
        return authorizationCodeVerifier;
      }

      let array = new Uint32Array(64);
      window.crypto.getRandomValues(array);

      authorizationCodeVerifier = Array.from(array, dec2hex).join("");

      userStore.setAuthorizationCodeVerifier(authorizationCodeVerifier);

      return authorizationCodeVerifier;
    };

    const getAuthorizationCodeChallenge = async (verifier) => {
      const hashed = await sha256(verifier);
      return base64urlencode(hashed);
    };

    app.config.globalProperties.$userAuthenticator = {
      authorize,
      getAuthorizationCodeVerifier,
      getAuthorizationCodeChallenge,
      refreshAccessToken,
      getAccessToken,
      isAccessTokenValid,
      isAuthorized,
      logout,
      resetStores,
    };
  },
};
